/**
 * # Chip
 *
 * A customizable chip component.
 *
 * ## Props
 *
 * - `as` (optional): The HTML element or React component to render as the wrapper element. Defaults to `'span'`.
 * - `checked` (optional): Specifies whether the chip is checked. Defaults to `false`.
 * - `children` (optional): The content of the chip.
 * - `className` (optional): Additional CSS class names for the chip.
 * - `color`: The color variant of the chip. Must be one of `'gray'`, `'blue'`, or `'purple'`.
 * - `size`: The size variant of the chip. Must be one of `'small'` or `'default'`.
 * - `disabled` (optional): Specifies whether the chip is disabled. Defaults to `false`.
 * - `error` (optional): Specifies whether the chip has an error state. Defaults to `false`.
 * - `hasFocus` (optional): Specifies whether the chip has focus. Defaults to `false`.
 * - `hasHover` (optional): Specifies whether the chip is being hovered. Defaults to `false`.
 *
 * ## Usage
 *
 * ```tsx
 * import Chip from './Chip.react';
 *
 * function App() {
 *   return (
 *     <Chip color="blue" size="default">
 *       Hello, World!
 *     </Chip>
 *   );
 * }
 * ```
 */

import React, {forwardRef} from 'react';
import classnames from 'classnames';

import './chip.scss';

interface Props<C extends React.ElementType> {
  as?: C;
  checked?: boolean;
  children?: React.ReactNode;
  className?: string;
  color: 'gray' | 'blue' | 'purple';
  size: 'small' | 'default';
  disabled?: boolean;
  error?: boolean;
  hasFocus?: boolean;
  hasHover?: boolean;
}

type ChipProps<C extends React.ElementType> = Props<C> &
  Omit<React.ComponentPropsWithRef<C>, keyof Props<C>>;

function Chip<C extends React.ElementType = 'span'>(
  {
    as,
    checked,
    children,
    className,
    disabled,
    error,
    color = 'gray',
    size = 'default',
    hasFocus,
    hasHover,
    role,
    ...rest
  }: ChipProps<C>,
  ref
) {
  const WrapperComponent = as || 'span';
  return (
    <WrapperComponent
      className={classnames('a-chip', className, {
        [`a-chip--${color}`]: color,
        [`a-chip--${size}`]: size,
        'a-chip--disabled': disabled,
        'a-chip--error': error,
        'a-chip--focus': hasFocus,
        'a-chip--hover': hasHover,
        'a-chip--selected': checked
      })}
      checked={checked}
      disabled={disabled}
      role={role}
      ref={ref}
      {...rest}
    >
      {children}
    </WrapperComponent>
  );
}

export default forwardRef(Chip);
